// @owners { team: patients }
import {
  AFREEZA_WAIVED_FEE_SKUS,
  BLUJEPA_WAIVED_FEE_SKU,
  FUROSCIX_WAIVED_FEE_SKUS,
  IBSRELA_WAIVED_FEE_SKU,
  JOURNAVX_WAIVED_FEE_SKU,
  NATROBA_WAIVED_FEE_SKU,
  NY_WAIVED_FEE_SKUS,
  ORLYNVAH_WAIVED_FEE_SKU,
  PIVYA_WAIVED_FEE_SKU,
  SAME_DAY_DELIVERY_FEE_PILOT_FACILITY_IDS,
  SPINOSAD_WAIVED_FEE_SKUS,
  TAMIFLU_WAIVED_FEE_SKU,
  TWIIST_WAIVED_FEE_SKUS,
  VTAMA_WAIVED_FEE_SKU,
  XOFLUZA_WAIVED_FEE_SKUS,
  ZURZUVAE_WAIVED_FEE_SKUS,
} from '~shared/constants';
import { getRemovingDeliveryWillAddDeliveryFee } from '~shared/features/same-day-delivery/selectors/getRemovingDeliveryWillAddDeliveryFee';
import { getDeliveryFeeFromShipment } from '~shared/features/shipment-fees/selectors/getShipmentFeesFromShipment';
import { getShipmentAddress, getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getIsFertilityUser } from '~shared/features/users/selectors/getIsFertilityUser';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { getFormattedPrice } from '~shared/helpers/pricingBreakdown';
import { useOrderPricing, useQueryShipmentFeesAvailable, useQueryShipmentFeesConfig } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { type DeliveryWithPrescription } from '~shared/types';

const DELIVERY_FEE_SUFFIX = (formattedFee: string) =>
  `This change would result in a ${formattedFee} delivery fee being added to your order.`;

type ErrorMessageFactory = (formattedFee: string, threshold?: string) => string;

/** NDC -> message factory for O(1) lookup. Built in priority order so first assignment wins. */
const NDC_WAIVE_MESSAGE_MAP: Record<string, ErrorMessageFactory> = {};

const setErrorMessageForNDC = (ndcs: string | readonly string[], factory: ErrorMessageFactory) => {
  const list = typeof ndcs === 'string' ? [ndcs] : [...ndcs];
  for (const ndc of list) {
    if (!(ndc in NDC_WAIVE_MESSAGE_MAP)) {
      NDC_WAIVE_MESSAGE_MAP[ndc] = factory;
    }
  }
};

setErrorMessageForNDC(
  AFREEZA_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Afreeza. ${DELIVERY_FEE_SUFFIX(fee)}`,
);

setErrorMessageForNDC(
  BLUJEPA_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Blujepa. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  FUROSCIX_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Furoscix. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  IBSRELA_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Ibsrela. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  JOURNAVX_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Journavx. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  NATROBA_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Natroba. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  ORLYNVAH_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Orlynvah. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  PIVYA_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Pivya. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  SPINOSAD_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Spinosad. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  TAMIFLU_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Tamiflu. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  TWIIST_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Twiist. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  VTAMA_WAIVED_FEE_SKU,
  (fee) => `Delivery fees are waived if your shipment includes Vtama. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  XOFLUZA_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Xofluza. ${DELIVERY_FEE_SUFFIX(fee)}`,
);
setErrorMessageForNDC(
  ZURZUVAE_WAIVED_FEE_SKUS,
  (fee) => `Delivery fees are waived if your shipment includes Zurzuvae. ${DELIVERY_FEE_SUFFIX(fee)}`,
);

/** O(1) lookup for NY COVID waive NDCs (message only shown when facility is NY). */
const NY_WAIVED_FEE_NDC_SET = new Set(NY_WAIVED_FEE_SKUS);

const COVID_WAIVE_MESSAGE: ErrorMessageFactory = (fee) =>
  `Delivery fees are waived if your shipment includes Paxlovid, Lagevrio, or Evusheld. ${DELIVERY_FEE_SUFFIX(fee)}`;

type WaiveFeeConfig = {
  ndc: string;
  facilityId: number | undefined;
  removedPrescriptionBelowDeliveryFeeMinimum: boolean;
  isClinicPayPrescription: boolean;
  minimumThresholdToWaiveFeeDisplay: string | undefined;
};

const getErrorMessage = (config: WaiveFeeConfig, deliveryFee: number | undefined): string => {
  const formattedDeliveryFee = getFormattedPrice(deliveryFee);

  if (config.isClinicPayPrescription) {
    return `Delivery fees are waived if your shipment includes a medication covered by clinic voucher. ${DELIVERY_FEE_SUFFIX(formattedDeliveryFee)}`;
  }
  if (config.removedPrescriptionBelowDeliveryFeeMinimum && config.minimumThresholdToWaiveFeeDisplay) {
    return `This would make your subtotal under ${config.minimumThresholdToWaiveFeeDisplay}. ${DELIVERY_FEE_SUFFIX(formattedDeliveryFee)}`;
  }
  if (
    config.facilityId === SAME_DAY_DELIVERY_FEE_PILOT_FACILITY_IDS.NEW_YORK &&
    NY_WAIVED_FEE_NDC_SET.has(config.ndc)
  ) {
    return COVID_WAIVE_MESSAGE(formattedDeliveryFee);
  }

  const messageFactory = NDC_WAIVE_MESSAGE_MAP[config.ndc];
  if (messageFactory) {
    return messageFactory(formattedDeliveryFee, config.minimumThresholdToWaiveFeeDisplay);
  }
  return DELIVERY_FEE_SUFFIX(formattedDeliveryFee);
};

type Props = {
  delivery: DeliveryWithPrescription;
};

export const usePrescriptionDeliveryWaiveFeeWarning = ({ delivery }: Props) => {
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID: delivery?.shipment_id }));
  const facilityId = address?.dispensing_facility_id;
  const currentUserID = useSelectorShared(getCurrentUserID);
  const isFertilityUser = useSelectorShared((state) => getIsFertilityUser(state, { userID: currentUserID }));
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID: delivery?.shipment_id }));
  const { orderPricing } = useOrderPricing({ shipmentID: shipment?.id });
  const deliveryFee = shipment && getDeliveryFeeFromShipment(shipment);
  const { shipmentFeeConfigHash } = useQueryShipmentFeesConfig();
  const { shipmentFeeAvailableHash } = useQueryShipmentFeesAvailable({
    delivery: {
      shipmentId: shipment?.id,
      addressId: delivery?.address_id,
      removedPrescriptionId: delivery?.prescription.id,
    },
  });
  const availableFeeAmount = deliveryFee?.fee_type && shipmentFeeAvailableHash[deliveryFee?.fee_type]?.amount;
  const removalWillIncurFee = (availableFeeAmount || 0) > 0;
  const hasDeliveryFeeWaived = deliveryFee?.amount === 0;
  const feeConfigData = deliveryFee?.fee_type && shipmentFeeConfigHash[deliveryFee?.fee_type];
  const minimumThresholdToWaiveFee = feeConfigData?.minimum_threshold_to_waive_fee;
  const ndc = delivery?.prescription?.ndc || '';
  const isClinicPayPrescription = !!delivery?.prescription?.covered_by_voucher_payer;
  const removedPrescriptionBelowDeliveryFeeMinimum = useSelectorShared((state) =>
    minimumThresholdToWaiveFee && isFertilityUser
      ? getRemovingDeliveryWillAddDeliveryFee(state, {
          deliveryID: delivery?.id,
          shipmentID: delivery?.shipment_id,
          minimumThresholdToWaiveFee: minimumThresholdToWaiveFee.value,
          orderPricing,
        })
      : false,
  );
  const message = getErrorMessage(
    {
      ndc,
      facilityId,
      removedPrescriptionBelowDeliveryFeeMinimum,
      isClinicPayPrescription,
      minimumThresholdToWaiveFeeDisplay: minimumThresholdToWaiveFee?.display_value,
    },
    availableFeeAmount,
  );
  const showDeliveryWaiveFeeWarning = hasDeliveryFeeWaived && removalWillIncurFee;

  return { message, showDeliveryWaiveFeeWarning };
};
